import Module from '../../module';
import scroll from '../../../libs/scroll/scroll';
import ChartLib from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

interface OptionsInterface {
	cssId: {
		heHubPulsarsDiscoveryChartChartCanvasId?: string;

		anim?: {
			heHubPulsarsDiscoveryChartContentSectionHeaderCls: string;
			heHubPulsarsDiscoveryChartContentSectionDisclaimerCls: string;
		}
	},

	slug: string
}

class Chart extends Module {

	publicVars: OptionsInterface;
	private chartInstance: ChartLib | null = null;

	constructor(options: OptionsInterface) {
		super('He Hub Pulsars Discovery Chart Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(
			this.publicVars.cssId.anim?.heHubPulsarsDiscoveryChartContentSectionHeaderCls || '',
			'he_hub_pulsars_discovery_chart', { animCls: 'animate__fadeInLeft25P', once: true });

		scroll.registerAnimatedElement(
			this.publicVars.cssId.anim?.heHubPulsarsDiscoveryChartContentSectionDisclaimerCls || '',
			'he_hub_pulsars_discovery_chart', { animCls: 'animate__fadeIn', once: true });

		ChartLib.register(ChartDataLabels);

		this.buildChart();
	}

	bind() {
		this.msg('bind');

		let ticking = false;

		window.addEventListener('resize', () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					this.buildChart();
					ticking = false;
				});
			}
			ticking = true;
		});
	}

	buildChart() {
		const chartData = {
			labels: ['#PLSR Jetstream #1', 'Lunar Regolith'],
			datasets: [
				{
					label: 'Low PPB Value',
					data: [1.3, 1.4], // Replace with your actual Low PPB values
					backgroundColor: '#cc0000', // Darker/Primary Pulsar Green
					borderColor: '#cc0000',
					borderWidth: 1,
					borderRadius: 2,
					barPercentage: 0.95,
					categoryPercentage: 0.4,
				},
				{
					label: 'High PPB Value',
					data: [14.5, 15], // Replace with your actual High PPB values
					backgroundColor: '#107539', // Lighter accent green / secondary color
					borderColor: '#107539',
					borderWidth: 1,
					borderRadius: 2,
					barPercentage: 0.95,
					categoryPercentage: 0.4
				}
			],

		};

		const canvasElementHtml = document.querySelector(
			this.publicVars.cssId.heHubPulsarsDiscoveryChartChartCanvasId!) as HTMLCanvasElement;

		if (!canvasElementHtml) return;

		if (this.chartInstance) {
			this.chartInstance.destroy();
		}

		const displayTitles = window.innerWidth > 600;

		let xTicksFontSize = 18;

		if(window.innerWidth > 380) {
			xTicksFontSize = 16;
		} else if(window.innerWidth > 360) {
			xTicksFontSize = 14;
		} else if(window.innerWidth > 340) {
			xTicksFontSize = 13;
		} else {
			xTicksFontSize = 12;
		}

		let delayed: boolean;

		this.chartInstance = new ChartLib(
			canvasElementHtml,
			{
				type: 'bar',
				data: chartData,
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: {
							display: true,
							position: 'top',
							labels: {
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 14,
								},
								boxWidth: 16,
								padding: 16
							}
						},
						tooltip: {
							callbacks: {
								label: (context) => ` ${context.dataset.label}: ${context.parsed.y} PPB`
							}
						},
						datalabels: {
							display: false // Set to true if you want labels on top of bars
						}
					},
					animation: {
						onComplete: () => {
							delayed = true;
						},
						delay: (context) => {
							let delay = 0;
							if (context.type === 'data' && context.mode === 'default' && !delayed) {
								delay = context.dataIndex * 150 + context.datasetIndex * 100;
							}
							return delay;
						},
					},
					scales: {
						x: {
							title: {
								display: false,
								text: 'Category',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 16,
									weight: 500,
								},
								padding: { top: 15 }
							},
							ticks: {
								color: 'rgba(255, 255, 255, 1)',
								font: {
									family: 'source-sans-pro',
									size: xTicksFontSize,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: 10
							},
							grid: {
								display: false
							}
						},
						y: {
							beginAtZero: true,
							title: {
								display: displayTitles,
								text: 'PPB',
								color: '#ffffff',
								font: {
									family: 'source-sans-pro',
									size: 18,
									weight: 500,
									lineHeight: 1.2,
								},
								padding: { bottom: 15 }
							},
							border: {
								display: false,
								color: 'rgba(255, 255, 255, 0.2)'
							},
							grid: {
								display: true,
								drawOnChartArea: true,
								drawTicks: false,
								color: 'rgba(255, 255, 255, 0.1)',
								z: -1
							},
							ticks: {
								color: 'rgba(255, 255, 255, 0.6)',
								stepSize: 5,
								callback: function (value) {
									return `${value}`;
								}
							}
						}
					}
				}
			}
		);
	}
}

export default Chart;